/**
 * Clamps a given zoom value within a specified range.
 *
 * @param {number} zoomValue - The zoom value to be clamped.
 * @returns {number} - The clamped zoom value, constrained between the minimum and maximum zoom levels.
 */
function clampScaling(zoomValue) {
  const minZoom = 1
  const maxZoom = 150
  return Math.max(minZoom, Math.min(maxZoom, zoomValue))
}

/**
 * Rotates the base rotation value 90 degrees to the left or right.
 * Ensures the rotation value remains within the range of 0 to 359 degrees.
 *
 * @param {number} baseRotation - The current rotation value in degrees.
 * @param {string} direction - The direction to rotate ('left' or 'right'). Defaults to 'left'.
 * @returns {number} - The new rotation value after applying the rotation.
 */
function rotate(baseRotation, direction = 'left') {
  let r = baseRotation
  r = direction === 'left' ? r - 90 : r + 90
  if (r < 0) r += 360
  if (r >= 360) r -= 360
  return r
}

/**
 * calculate the distance between two pointers/fingers
 * @param p1
 * @param p2
 * @returns the distance between the two touch points
 */
function getDistance(p1, p2) {
  return Math.hypot(p2.x - p1.x, p2.y - p1.y)
}

/**
 * calculate the center point of two pointers/fingers
 * @param p1
 * @param p2
 * @returns center point of the two touch points
 */
function getCenter(p1, p2) {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  }
}

/**
 * Calculates the relative position of a pointer within a given container (stage).
 * The function takes into account the container's bounding rectangle to determine
 * the pointer's position relative to the container's top-left corner within the viewport.
 *
 * @param {HTMLElement} container - The container element to calculate the relative position within.
 * @param {Touch | MouseEvent} touch - The pointer event (e.g., touch or mouse event)
 *                                      containing the coordinates to be converted.
 * @returns {Object} An object containing the relative `x` and `y` coordinates.
 */
function getRelativePointerPosition(container, touch) {
  const rect = container.getBoundingClientRect()
  return {
    x: touch.clientX - rect.left,
    y: touch.clientY - rect.top,
  }
}

/**
 * Detects if the scroll wheel event is triggered by a trackpad.
 *
 * This function determines whether the input event is likely coming from a trackpad
 * based on the `wheelDeltaY`, `deltaY`, and `deltaMode` properties of the event.
 * source: https://stackoverflow.com/a/62415754
 *
 * @param {WheelEvent} e - The wheel event
 * @returns {boolean} - Returns `true` if the event is likely from a trackpad, otherwise `false`
 */
function detectTrackPad(e) {
  if (e.wheelDeltaY) {
    return e.wheelDeltaY === e.deltaY * -3
  }
  if (e.deltaMode === 0) {
    return true
  }
  return false
}

export { clampScaling, rotate, getDistance, getCenter, getRelativePointerPosition, detectTrackPad }
